<template>
    <charges-find
        :params="{
            charge: $route.params.charge,
            include: 'payment_intent,fees(include:property),rents(include:property)'
        }"
        v-slot="{
            sid,
            data: charge,
            error,
            is_loading,
            has_loaded,
            find
        }"
    >
        <div v-if="!has_loaded || error">
            <base-loading
                :error="error"
                :retry="find"
                class="py-6"
            />

            <error-modal
                v-if="error"
                :error="error"
            />
        </div>

        <template v-else>
            <div class="mb-6 bg-white rounded shadow">
                <base-form @submit="onSubmit(charge)">
                    <header class="flex items-center px-4 py-3 border-b sm:px-6 md:py-4">
                        <h1 class="text-lg font-normal">
                            Confirm Payment
                        </h1>
                    </header>

                    <div class="px-4 pt-6 border-b md:px-6">
                        <validator-alert :sid="sid" />

                        <div class="text-grey-darker">
                            <p class="mb-3">
                                This Smart Payment requires further action. You must manually confirm this payment.
                            </p>

                            <p class="mb-3">
                                <strong>You have not been charged yet</strong> - we will complete the charge after
                                you have successfully confirmed this payment.
                            </p>

                            <p class="mb-3">
                                For further information, please refer to the following article:
                            </p>

                            <ul class="mb-6">
                                <li>
                                    <a
                                        href="#"
                                        class="text-blue"
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        Why do I have to manually confirm payments?

                                        <base-svg
                                            name="icon-external-window"
                                            class="inline w-4 h-4 fill-primary-blue fill-secondary-blue"
                                        />
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <h6 class="mb-3 text-grey-darkest">Payment Summary</h6>

                        <chargables-table
                            :rents="charge.rents.data"
                            :fees="charge.fees.data"
                            class="mb-6 border rounded"
                        >
                            <template
                                v-slot:header="{
                                    width
                                }"
                            >
                                <table-ish-row-header class="rounded-t">
                                    <table-ish-cell
                                        v-if="width > 500"
                                        :shrink="true"
                                    >
                                        Item
                                    </table-ish-cell>

                                    <table-ish-cell>
                                        Description
                                    </table-ish-cell>

                                    <table-ish-cell :shrink="true">
                                        Amount
                                    </table-ish-cell>
                                </table-ish-row-header>
                            </template>

                            <template
                                v-slot:footer="{
                                    width
                                }"
                            >
                                <table-ish-row>
                                    <table-ish-cell v-if="width > 500" />

                                    <table-ish-cell
                                        class="text-right text-grey-darker"
                                    >
                                        Total
                                    </table-ish-cell>

                                    <table-ish-cell
                                        :shrink="true"
                                        class="font-semibold"
                                    >
                                        {{ charge.amount | money }}
                                    </table-ish-cell>
                                </table-ish-row>
                            </template>
                        </chargables-table>
                    </div>

                    <base-form-footer
                        :show-required="false"
                        class="rounded-b"
                    >
                        <base-button
                            :is-loading="is_saving"
                            class="w-full btn-blue md:w-auto"
                            :disabled="charge.state !== 'unconfirmed'"
                        >
                            Confirm Payment
                        </base-button>
                    </base-form-footer>

                    <modal-success :is-open="show_succeeded_modal">
                        <template slot="heading">Payment successful!</template>

                        <p>
                            Thanks for your payment! We'll send you a confirmation email.
                        </p>

                        <p class="mt-3 text-sm">
                            p.s. It may take a moment for this payment appear on your dashboard and be reflected on the tenancy's balance.
                        </p>

                        <div
                            slot="footer"
                            class="flex flex-col md:flex-row-reverse"
                        >
                            <router-link
                                :to="{
                                    name: $router.fix('dashboard.*.charges.show'),
                                    params: {
                                        charge: charge.uuid
                                    }
                                }"
                                class="text-center btn btn-green"
                            >
                                Go to Payment
                            </router-link>
                        </div>
                    </modal-success>

                    <modal-error
                        :is-open="show_failed_modal"
                        @close="show_failed_modal = false"
                    >
                        <template slot="heading">Payment Failed</template>

                        <p>
                            We were unable to process your payment. We've sent you
                            an email confirming this.
                        </p>

                        <div
                            slot="footer"
                            slot-scope="{ close }"
                            class="flex flex-col md:flex-row-reverse"
                        >
                            <button
                                type="button"
                                class="text-center btn btn-red"
                                @click.prevent="close"
                            >
                                Try Again
                            </button>
                        </div>
                    </modal-error>
                </base-form>
            </div>
        </template>
    </charges-find>
</template>

<script>
    import ChargesFind from '../../../../../components/charges-find'
    import ChargablesTable from '../../../../../components/chargables-table'

    export default {
        components: {
            ChargesFind,
            ChargablesTable
        },
        data () {
            return {
                is_saving: false,
                show_succeeded_modal: false,
                show_failed_modal: false
            }
        },
        methods: {
            onSubmit (charge) {
                this.is_saving = true

                this.$stripe.confirmCardPayment(charge.payment_intent.client_secret, {
                    payment_method: charge.payment_intent.payment_method
                })
                    .then(result => {
                        this.is_saving = false

                        if (result.error) {
                            this.show_failed_modal = true
                        } else {
                            this.show_succeeded_modal = true
                        }
                    })
            }
        }
    }
</script>
